import {
  EMPLOYEE_FIELDS,
  normaliseHeader,
  rowHasBankingData,
  rowHasStatutoryData,
} from "./employeeFieldSchema";
import { DEFAULT_EMPLOYEE_TYPE, isValidEmployeeType, normalizeEmployeeType } from "./employeeTypeConstants";
import { normalizeEmployeeRole } from "./employeeRoleConstants";
import { validateDateOfBirth } from "../../components/common/datePickerUtils";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const EMAIL_RE =
  // pragmatic (not RFC-perfect) and matches backend EmailStr expectations well
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export function isBlank(v) {
  return v === null || v === undefined || String(v).trim() === "";
}

function parseBool(value) {
  const s = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y"].includes(s)) return true;
  if (["false", "0", "no", "n"].includes(s)) return false;
  return null;
}

export function validateValue(type, value, field) {
  if (isBlank(value)) return null;

  const s = String(value).trim();
  if (type === "string") return null;
  if (type === "email") return EMAIL_RE.test(s) ? null : "Invalid email format";
  if (type === "uuid") return UUID_RE.test(s) ? null : "Invalid UUID format";
  if (type === "date") {
    // backend expects YYYY-MM-DD
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "Use YYYY-MM-DD format";
    const d = new Date(s + "T00:00:00");
    if (Number.isNaN(d.getTime())) return "Invalid date";
    return null;
  }
  if (type === "number") return Number.isFinite(Number(s)) ? null : "Invalid number";
  if (type === "phone") {
    if (/[a-zA-Z]/.test(s)) return "Phone number cannot contain letters — digits only";
    if (!/^[0-9+\-\s()]+$/.test(s)) return "Invalid phone format — use digits only (e.g. 9876543210)";
    const digits = s.replace(/\D/g, "");
    if (digits.length < 10) return "Phone must have at least 10 digits";
    if (digits.length > 15) return "Phone number is too long";
    return null;
  }
  if (type === "boolean") {
    return parseBool(s) === null ? "Use true/false, yes/no, or 1/0" : null;
  }
  if (type === "ifsc") {
    const code = s.toUpperCase().replace(/\s+/g, "");
    return IFSC_RE.test(code) ? null : "IFSC must look like HDFC0001234";
  }
  if (type === "account_number") {
    const digits = s.replace(/\D/g, "");
    if (digits.length < 9 || digits.length > 18) return "Account number must be 9–18 digits";
    return null;
  }
  if (type === "uan" || type === "aadhaar") {
    const digits = s.replace(/\D/g, "");
    return digits.length === 12 ? null : `${type === "uan" ? "UAN" : "Aadhaar"} must be exactly 12 digits`;
  }
  if (type === "pan") {
    const pan = s.toUpperCase().replace(/[^A-Z0-9]/g, "");
    return PAN_RE.test(pan) ? null : "PAN must match format AAAAA9999A";
  }
  if (type === "esic") {
    const digits = s.replace(/\D/g, "");
    return digits.length === 10 ? null : "ESIC number must be exactly 10 digits";
  }
  if (type === "enum") {
    const allowed = (field?.enumValues || []).map((v) => String(v).toLowerCase());
    if (field?.key === "employee_type") {
      return isValidEmployeeType(s) ? null : `Invalid value. Allowed: ${(field?.enumValues || []).join(", ")}`;
    }
    return allowed.includes(String(s).toLowerCase())
      ? null
      : `Invalid value. Allowed: ${(field?.enumValues || []).join(", ")}`;
  }
  if (type === "fk") return UUID_RE.test(s) ? null : "Provide UUID or map from name";
  return null;
}

function buildLookupIndex(list, nameKey) {
  const map = new Map();
  for (const item of list || []) {
    const name = String(item?.[nameKey] ?? "").trim().toLowerCase();
    if (!name) continue;
    // prefer first seen to keep deterministic
    if (!map.has(name)) map.set(name, item);
  }
  return map;
}

export function resolveFk({ field, value, record, lookups }) {
  // If a UUID is provided and exists in lookup list -> ok.
  // Else attempt name mapping via secondaryNameKey or by matching to list names.
  const list = lookups?.[field.fk?.kind] || [];
  const idKey = field.fk?.idKey || "id";
  const nameKey = field.fk?.nameKey || "name";
  const valueStr = isBlank(value) ? "" : String(value).trim();

  if (UUID_RE.test(valueStr)) {
    // If we have a list, ensure it exists; otherwise just accept UUID format.
    if (list.length === 0) return { ok: true, value: valueStr };
    const exists = list.some((x) => String(x?.[idKey]) === valueStr);
    return exists
      ? { ok: true, value: valueStr }
      : { ok: false, error: `Unknown ${field.label} ID` };
  }

  const secondaryKey = field.bulk?.secondaryNameKey;
  const nameCandidate = secondaryKey ? record?.[secondaryKey] : null;
  const name = !isBlank(nameCandidate) ? String(nameCandidate).trim() : valueStr;
  if (isBlank(name)) return { ok: true, value: undefined };

  if (list.length === 0) {
    return { ok: false, error: `${field.label} list is empty; provide UUID` };
  }

  const idx = buildLookupIndex(list, nameKey);
  const hit = idx.get(String(name).trim().toLowerCase());
  if (!hit) return { ok: false, error: `Unknown ${field.label}: ${name}` };
  return { ok: true, value: String(hit[idKey]) };
}

export function getEmployeeRulesContext({ lookups }) {
  const requiredIfHasOptions = (kind) => (lookups?.[kind] || []).length > 0;
  return {
    requireDepartment: requiredIfHasOptions("departments"),
    requireDesignation: requiredIfHasOptions("designations"),
    requireLocation: requiredIfHasOptions("locations"),
  };
}

export function validateEmployeeRecord(record, { mode, lookups } = {}) {
  const errors = {};
  const ctx = getEmployeeRulesContext({ lookups });

  for (const field of EMPLOYEE_FIELDS) {
    const v = record?.[field.key];

    const isRequired =
      mode === "bulk"
        ? !!field.bulk?.required || (field.key === "department_id" && ctx.requireDepartment) ||
          (field.key === "designation_id" && ctx.requireDesignation) ||
          (field.key === "location_id" && ctx.requireLocation)
        : !!field.required ||
          (field.key === "department_id" && ctx.requireDepartment) ||
          (field.key === "designation_id" && ctx.requireDesignation) ||
          (field.key === "location_id" && ctx.requireLocation);

    if (isRequired && isBlank(v)) {
      errors[field.key] = `${field.label} is required`;
      continue;
    }

    if (field.type === "fk") {
      if (isBlank(v) && !isRequired) continue;
      const res = resolveFk({ field, value: v, record, lookups });
      if (!res.ok) errors[field.key] = res.error;
      continue;
    }

    if (field.key === "date_of_birth" && !isBlank(v)) {
      const dobErr = validateDateOfBirth(v);
      if (dobErr) errors.date_of_birth = dobErr;
      continue;
    }

    if (field.key === "annual_ctc" && !isBlank(v)) {
      const n = Number(v);
      if (!Number.isFinite(n)) errors.annual_ctc = "Salary must be a valid number";
      else if (n <= 0) errors.annual_ctc = "Salary must be greater than zero";
      continue;
    }

    if (field.key === "role" && !isBlank(v)) {
      const normalized = normalizeEmployeeRole(v);
      if (!normalized) errors.role = "Invalid system role";
      continue;
    }

    const err = validateValue(field.type === "fk" ? "uuid" : field.type, v, field);
    if (err) errors[field.key] = err;
  }

  // Banking is optional, but if any banking cell is filled require IFSC + account number.
  if (mode === "bulk" && rowHasBankingData(record)) {
    if (isBlank(record?.ifsc_code)) errors.ifsc_code = "IFSC Code is required when banking details are provided";
    if (isBlank(record?.account_number)) {
      errors.account_number = "Account Number is required when banking details are provided";
    }
  }

  // Statutory IDs are optional; if any ID is present, effective_from defaults on coerce,
  // but when explicitly provided it must be a valid date (already checked above).
  if (mode === "bulk" && rowHasStatutoryData(record) && !isBlank(record?.statutory_effective_from)) {
    const effErr = validateValue("date", record.statutory_effective_from, { key: "statutory_effective_from" });
    if (effErr) errors.statutory_effective_from = effErr;
  }

  return errors;
}

export function coerceEmployeeRecord(record, { lookups } = {}) {
  const out = { ...record };

  // Track FK fields that resolved successfully so we can safely drop their
  // secondary name keys (department_name, designation_name, ...).
  // Unresolved ones keep the name so the verification UI can show the file value.
  const resolvedFkKeys = new Set();

  // FK mapping (id or name -> id)
  for (const field of EMPLOYEE_FIELDS.filter((f) => f.type === "fk")) {
    const res = resolveFk({ field, value: out[field.key], record: out, lookups });
    if (res.ok) {
      if (res.value === undefined) delete out[field.key];
      else out[field.key] = res.value;
      resolvedFkKeys.add(field.key);
    }
    // leave as-is if not ok (caller will validate)
  }

  if (isBlank(out.employee_type)) {
    out.employee_type = DEFAULT_EMPLOYEE_TYPE;
  } else {
    const normalized = normalizeEmployeeType(out.employee_type);
    if (normalized) out.employee_type = normalized;
  }

  // normalize common aliases
  if (!isBlank(out.work_email) && isBlank(out.email)) out.email = out.work_email;
  if (!isBlank(out.email) && isBlank(out.work_email)) out.work_email = out.email;
  if (!isBlank(out.mobile_phone) && isBlank(out.phone)) out.phone = out.mobile_phone;
  if (!isBlank(out.phone) && isBlank(out.mobile_phone)) out.mobile_phone = out.phone;

  if (!isBlank(out.role)) out.role = normalizeEmployeeRole(out.role);

  // Employee code is auto-generated — never send from bulk file
  delete out.employee_code;

  for (const field of EMPLOYEE_FIELDS) {
    // Only drop the secondary name key when the FK actually resolved to an ID.
    // This lets the verification UI keep showing the file's original name
    // (e.g. "Marketing") for the user to map manually.
    if (field.bulk?.secondaryNameKey && resolvedFkKeys.has(field.key)) {
      delete out[field.bulk.secondaryNameKey];
    }
  }

  // dates already kept as YYYY-MM-DD strings (backend parser handles in Pydantic for single create;
  // bulk service parses date strings explicitly)
  // booleans/numbers if present
  if (!isBlank(out.is_active)) {
    const s = String(out.is_active).trim().toLowerCase();
    out.is_active = s === "true" || s === "1" || s === "yes";
  }

  if (!isBlank(out.annual_ctc)) {
    const n = Number(out.annual_ctc);
    if (Number.isFinite(n)) out.annual_ctc = n;
  }

  if (isBlank(out.pay_frequency)) out.pay_frequency = "Monthly";
  if (isBlank(out.status)) out.status = "active";

  // Banking normalisation
  if (!isBlank(out.ifsc_code)) {
    out.ifsc_code = String(out.ifsc_code).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);
  }
  if (!isBlank(out.account_number)) {
    out.account_number = String(out.account_number).replace(/\D/g, "");
  }
  if (isBlank(out.account_holder_name) && rowHasBankingData(out)) {
    const name = [out.first_name, out.middle_name, out.last_name].filter(Boolean).join(" ").trim();
    if (name) out.account_holder_name = name;
  }

  // Statutory normalisation
  if (!isBlank(out.uan_number)) out.uan_number = String(out.uan_number).replace(/\D/g, "").slice(0, 12);
  if (!isBlank(out.aadhaar)) out.aadhaar = String(out.aadhaar).replace(/\D/g, "").slice(0, 12);
  if (!isBlank(out.esic_number)) out.esic_number = String(out.esic_number).replace(/\D/g, "").slice(0, 10);
  if (!isBlank(out.pan)) out.pan = String(out.pan).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
  if (!isBlank(out.pf_number)) out.pf_number = String(out.pf_number).replace(/[^A-Za-z0-9/-]/g, "").slice(0, 40);
  if (!isBlank(out.lwf_number)) out.lwf_number = String(out.lwf_number).replace(/[^A-Za-z0-9-]/g, "").slice(0, 40);

  if (rowHasStatutoryData(out)) {
    if (isBlank(out.employment_category)) out.employment_category = "employee";
    if (isBlank(out.statutory_effective_from) && !isBlank(out.date_of_joining)) {
      out.statutory_effective_from = out.date_of_joining;
    }
  }

  for (const boolKey of [
    "pf_member",
    "esi_applicable",
    "pt_applicable",
    "lwf_applicable",
    "bonus_applicable",
  ]) {
    if (!isBlank(out[boolKey])) {
      const parsed = parseBool(out[boolKey]);
      if (parsed !== null) out[boolKey] = parsed;
    }
  }

  // strip blanks (keep explicit booleans)
  Object.keys(out).forEach((k) => {
    if (typeof out[k] === "boolean") return;
    if (isBlank(out[k])) delete out[k];
  });

  return out;
}

export function autoMapColumns(headers) {
  const headerNorms = (headers || []).map((h) => ({
    raw: h,
    norm: normaliseHeader(h),
  }));

  const mapping = {}; // targetKey -> sourceHeaderRaw

  for (const f of EMPLOYEE_FIELDS) {
    const candidates = new Set([
      f.key,
      ...(f.bulk?.headerAliases || []),
      f.label,
    ].map(normaliseHeader));

    const hit = headerNorms.find((h) => candidates.has(h.norm));
    if (hit) mapping[f.key] = hit.raw;

    if (f.bulk?.secondaryNameKey) {
      const nameCandidates = new Set([
        f.bulk.secondaryNameKey,
        ...(f.bulk.secondaryHeaderAliases || []),
      ].map(normaliseHeader));
      const nameHit = headerNorms.find((h) => nameCandidates.has(h.norm));
      if (nameHit) mapping[f.bulk.secondaryNameKey] = nameHit.raw;
    }
  }

  return mapping;
}

